import DashboardLayout from "lib/components/layouts/dashboard";
import ClinicWidget from "lib/components/common/dashboard/clinic-widget";
import { Col, Row } from "react-bootstrap";
import { AppContext } from "lib/contexts/app-context";
import { useContext, useEffect, useState } from "react";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { isPatientUser } from "lib/utils/util";
import usePatients from "lib/hooks/usePatients";
import useClinics from "lib/hooks/useClinics";
import PatientCalendar from "lib/components/patient/patient-calender";
import moment from "moment";
import { momentLocalizer } from "react-big-calendar";
import SimpleCard from "lib/components/common/simple-card";
import { utcToLocalClock } from "lib/utils/date-utils";
import SimpleModal from "lib/components/common/simple-modal";
import PatientSessionBookingSummaryTile from "lib/components/common/patient/patient-session-booking-summary-tile";
import { toast } from "sonner";

const localizer = momentLocalizer(moment);

const AdminPatientsCalenderPage = () => {
  const { appState } = useContext(AppContext);
  const patientId = appState?.user_id;
  const { getBookingsDetails, getAllSessionBookingsDetailsByClinic, getAllSessionBookingsDetails, changeSessionBookingStatus, loading: patientsLoading } = usePatients();
  const { clinics, getClinics, loading, error } = useClinics();

  const [sessionBookingsDetails, setSessionBookingsDetails] = useState<
    SessionBookingDetails[]
  >([]);

  const [allSessionBookingsDetailsByClinic, setAllSessionBookingsDetailsByClinic] = useState<
    SessionBookingDetails[]
  >([]);

  const [showSessionModal, setShowSessionModal] = useState(false);

  const [selectedSession, setSelectedSession] =
    useState<PatientSessionBookingDetails | null>(null);

  const [selectedClinic, setSelectedClinic] = useState<ClinicDetails>(null);

  const BreadcrumbItems = [
    {
      label: "Dashboard",
      path: isPatientUser(appState?.userRole)
        ? PAGE_ROUTES.PATIENT.Dashboard
        : PAGE_ROUTES.PRACTITIONER.Dashboard,
    },
    {
      label: "Calender",
      path: PAGE_ROUTES.PATIENT.Calender,
    },
  ];

  useEffect(() => {
    getClinics(false);
  }, []);

  useEffect(() => {
    if (!selectedClinic?.clinic?.id) return;

    getAllSessionBookingsDetailsByClinic(selectedClinic?.clinic?.id, (bookingsessions) => {
      setAllSessionBookingsDetailsByClinic(bookingsessions);
    });
    console.log("Fetching session bookings for clinic:", selectedClinic?.clinic?.id);
  }, [selectedClinic?.clinic?.id]);

  useEffect(() => {
    getAllSessionBookingsDetails((bookingsessions) => {
      setSessionBookingsDetails(bookingsessions);
    });
  }, [patientId]);

  const handleCalendarEventClick = (event) => {

    if (event.resource.type === "SESSION_BOOKING") {
      setSelectedSession(event.resource);
      setShowSessionModal(true);
    }
  };

  // Handler to update session booking status using API
  const onChangeSessionBookingStatus = async (sessionId: string, newStatus: string) => {
    try {
      await changeSessionBookingStatus(appState.user_id, sessionId, newStatus);
      setSessionBookingsDetails((prev) =>
        prev.map((session) =>
          session.id === sessionId ? { ...session, status: newStatus } : session
        )
      );
      setShowSessionModal(false);
      toast.success(`Session status updated to '${newStatus}'.`);
    } catch (error) {
      toast.error('Failed to update session status. Please try again.');
    }
  };

  return (
    <DashboardLayout breadcrumbItems={BreadcrumbItems}>

      <Row className="overflow-auto mt-4 custom-scroll">
        {(clinics ?? []).map((clinic, index) => (
          <Col sm="3" key={clinic?.id}>
            <ClinicWidget
              clinic={clinic}
              className="cursor-pointer hover-shadow"
              onSelected={(value) => {
                setSelectedClinic(value);
              }}
              isSelected={selectedClinic?.clinic?.index - 1 === index}
            />
          </Col>
        ))}
      </Row>

      <SimpleCard
        title="Patients Sessions Booking Calender"
      >
        <PatientCalendar
          localizer={localizer}
          showAppointments={false}
          showSessionBookings={false}
          appointmentList={[]}
          sessionBookingList={allSessionBookingsDetailsByClinic.map(s => ({
            title: s?.therapyName ? `${s.therapyName}` : "Session Booking",
            start: utcToLocalClock(s.fromTime),
            end: new Date(utcToLocalClock(s.fromTime).getTime() + s.duration * 60000),
            resource: {
              ...s,
              type: "SESSION_BOOKING",
            },
          }))}
          onEventClick={handleCalendarEventClick}
        />
      </SimpleCard>

      <SimpleModal
        open={showSessionModal}
        close={() => setShowSessionModal(false)}
        title="Session Details"
        size="sm"
      >
        {selectedSession && (
          <PatientSessionBookingSummaryTile
            fromTime={selectedSession.fromTime}
            {...selectedSession}
            onChangeSessionBookingStatus={onChangeSessionBookingStatus}
          />
        )}
      </SimpleModal>
    </DashboardLayout>
  );
};

export default AdminPatientsCalenderPage;
